import { $localStorage } from "@tajawal/web-core-sdk-v2/lib/services/local-storage.service";
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
} from "@/constants/cache.constants";
import {
  SEARCH_BOX_CONSTANTS,
  SEARCH_BOX_STATE_PROPS,
} from "@tajawal/web-flights-sdk/lib/constants";
import _get from "lodash/get";
import _map from "lodash/map";
import _pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import _isEqual from "lodash/isEqual";
import _includes from "lodash/includes";
import {
  getDefaultDates,
  isInvalidDates,
} from "@tajawal/web-flights-sdk/lib/utils/flights-search-box.utils";
import { formatDate } from "./date.utils";
import { DATE_FORMAT_MAP } from "@/constants/date.constants";
import { $sessionStorage } from "@tajawal/web-core-sdk-v2/lib/services/session-storage.service";

function shouldInitializeWithProps({ state, props }) {
  const searchBoxProps = _pick(props, SEARCH_BOX_STATE_PROPS);
  return (
    !_isEmpty(searchBoxProps) &&
    !_isEqual(_pick(state, SEARCH_BOX_STATE_PROPS), searchBoxProps)
  );
}

export const FlightsSearchBoxStateCache = {
  getByKey(key: string, defaultValue: any = null): any {
    return _get(
      $localStorage.get(LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE),
      key,
      defaultValue,
    );
  },

  getDomesticFareCalendarState() {
    return $localStorage.get(LOCAL_STORAGE_KEYS.IS_FARE_CALENDAR_LAYOUT, false);
  },

  get() {
    return $localStorage.get(LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE);
  },

  syncStorageWithUrl(params: { bg?: string; s?: string }) {
    const storageState = this.get();
    const updatedState = {
      ...storageState,
      directFlightsOnly: params?.s === "0",
      hasBaggageOnly: params?.bg === "true",
    };
    $localStorage.set(LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE, updatedState);
  },

  // TODO: Remove the different keys for Domestic and international
  toggleFareCalendarState(isFareCalendarOpen: boolean) {
    const internationalSearchBoxState = $localStorage.get(
      LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE,
    );
    const updatedSearchBoxState = {
      ...internationalSearchBoxState,
      fareCalendar: isFareCalendarOpen,
    };
    $localStorage.set(
      LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE,
      updatedSearchBoxState,
    );

    $localStorage.set(
      LOCAL_STORAGE_KEYS.IS_FARE_CALENDAR_LAYOUT,
      isFareCalendarOpen,
    );

    $sessionStorage.set(SESSION_STORAGE_KEYS.FARE_CALENDAR_STATE_CHANGED, true);
  },

  didFareCalendarStateChange() {
    return Boolean(
      $sessionStorage.get(SESSION_STORAGE_KEYS.FARE_CALENDAR_STATE_CHANGED),
    );
  },

  set(state) {
    $localStorage.set(LOCAL_STORAGE_KEYS.FLIGHT_SEARCH_BOX_STATE, state);
  },

  getInitialState({ props, state }) {
    const storageState = this.get();
    const isFareCalendarOpen =
      this.getDomesticFareCalendarState() || storageState?.fareCalendar;

    const shouldUseProps =
      shouldInitializeWithProps({ props, state }) || _isEmpty(storageState);
    if (shouldUseProps) {
      return props;
    }
    return {
      ...storageState,
      fareCalendar: isFareCalendarOpen,
      fields: _map(storageState.legs, () => {
        return {
          origin: {
            isDirty: false,
          },
          destination: {
            isDirty: false,
          },
          departureDate: {
            isDirty: false,
          },
          returnDate: {
            isDirty: false,
          },
        };
      }),
      legs: _map(storageState.legs, (leg) => {
        const isCachedDatesInValid = isInvalidDates({
          departureDate: leg.departureDate,
          returnDate: leg.returnDate,
        });
        return {
          ...leg,
          ...(isCachedDatesInValid && {
            departureDate: formatDate(
              getDefaultDates().departureDate,
              DATE_FORMAT_MAP[SEARCH_BOX_CONSTANTS.REQUEST_DATE_FORMAT],
            ),
            returnDate: formatDate(
              getDefaultDates().returnDate,
              DATE_FORMAT_MAP[SEARCH_BOX_CONSTANTS.REQUEST_DATE_FORMAT],
            ),
          }),
          nearbyOrigin: false,
          nearbyDestination: false,
        };
      }),
      nonstop: false,
      showDimmer: false,
      searching: false,
    };
  },
};

export const RecentSearchCache = {
  getOrigin() {
    return $localStorage.get(LOCAL_STORAGE_KEYS.ORIGIN_RECENT_SEARCH);
  },

  setOrigin(searches: string[]) {
    $localStorage.set(LOCAL_STORAGE_KEYS.ORIGIN_RECENT_SEARCH, searches);
  },

  getDestination() {
    return $localStorage.get(LOCAL_STORAGE_KEYS.DESTINATION_RECENT_SEARCH);
  },

  setDestination(searches: string[]) {
    $localStorage.set(LOCAL_STORAGE_KEYS.DESTINATION_RECENT_SEARCH, searches);
  },

  save({
    originIata,
    destinationIata,
  }: {
    originIata: string;
    destinationIata: string;
  }) {
    const previousOriginSearches = this.getOrigin() || [];
    const previousDestinationSearches = this.getDestination() || [];
    if (!_includes(previousOriginSearches, originIata)) {
      RecentSearchCache.setOrigin([originIata, ...previousOriginSearches]);
    }

    if (!_includes(previousDestinationSearches, destinationIata)) {
      RecentSearchCache.setDestination([
        destinationIata,
        ...previousDestinationSearches,
      ]);
    }
  },
};
